import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  FormControlLabel,
  FormHelperText,
  IconButton,
  TextField,
  useTheme,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import DoneIcon from '@material-ui/icons/Done';
import DeleteIcon from '@material-ui/icons/DeleteOutline';
import { Controller, useForm } from 'react-hook-form';
import { useLocation, history } from 'umi';
import dayjs from 'dayjs';
import { OPT_DETAIL, OPT_EDIT, OPT_TYPE } from '@/constants';
import DateRangePickerML from '@/components/DateRangePickerML';
import DataTableNativeML from '@/components/DataTableNativeML';
import ButtonTip from '@/components/buttonTip/ButtonTip';
import CouponModal from './components/CouponModal';
import {
  queryActivityConfig,
  addActivityConfig,
  updateActivityConfig,
} from './services';
import AccountActivityDetailStyled from './AccountActivityDetailStyled';

const getPageTitle = (opt) => {
  if (opt === OPT_DETAIL) {
    return '活动详情';
  }
  return OPT_TYPE[opt]?.title + '活动';
};

export default () => {
  const theme = useTheme();
  const location = useLocation();
  const { query } = location;
  const { opt } = query;
  const isDetail = opt === OPT_DETAIL;
  const { handleSubmit, control, setValue, errors } = useForm({
    defaultValues: {
      name: '',
      date: { startDate: null, endDate: null },
      code: '',
      couponRelated: false,
    },
  });
  const [detail, setDetail] = useState({});
  const [openExitDialog, setOpenExitDialog] = useState(false);
  const [openCouponModal, setOpenCouponModal] = useState(false);
  const [couponList, setCouponList] = useState([]);
  const formRef = useRef(null);

  const handleFinishClick = () => {
    if (formRef.current) {
      formRef.current.requestSubmit
        ? formRef.current.requestSubmit()
        : formRef.current.dispatchEvent(
            new Event('submit', { cancelable: true, bubbles: true }),
          );
    }
  };

  useEffect(() => {
    if (!query?.code) {
      return;
    }
    queryActivityConfig({ code: query.code }).then((res) => {
      const data = res.data;
      setDetail(data);
      setValue('name', data.name);
      setValue('date', {
        startDate: dayjs(data.dateBegin).format('YYYY-MM-DD'),
        endDate: dayjs(data.dateEnd).format('YYYY-MM-DD'),
      });
      setValue('code', data.code);
      setValue('couponRelated', Boolean(data.coupons?.length));
      setValue('couponList', data.coupons || []);
      setCouponList(data.coupons || []);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query?.code]);

  const handleSubmitSuccess = async (data) => {
    const values = {
      name: data.name,
      dateBegin: data.date.startDate + ' 00:00:00',
      dateEnd: data.date.endDate + ' 23:59:59',
      code: data.code,
      coupons: data.couponRelated ? couponList : [],
    };
    const request = opt === OPT_EDIT ? updateActivityConfig : addActivityConfig;
    await request(values);
    window.message.toast({
      type: 'success',
      message: '提交成功',
    });
  };

  const handleSubmitFail = () => {};

  const handleExitClick = () => {
    setOpenExitDialog(true);
  };

  const handleExitConfirm = () => {
    setOpenExitDialog(false);
    history.goBack();
  };

  const handleAddCoupon = () => {
    setOpenCouponModal(true);
  };

  const handleCloseCouponModal = () => {
    setOpenCouponModal(false);
  };

  const handleCouponConfirm = (selectedList) => {
    const list = selectedList.map((item) => ({
      id: item.id,
      couponId: item.id,
      couponName: item.title,
    }));
    setCouponList(list);
    setValue('couponRelated', true);
  };

  const handleDeleteCoupon = (id) => {
    setCouponList((prev) => (prev || []).filter((item) => item.id !== id));
  };

  const validateDateRange = (value) => {
    if (!value || !value.startDate || !value.endDate) {
      return '日期不得为空';
    }
    const diff = dayjs(value.endDate).diff(dayjs(value.endDate), 'month', true);
    if (diff > 3) {
      return '日期跨度不可超过三个月';
    }
    return true;
  };

  const couponTableColumns = [
    {
      title: '优惠券ID',
      align: 'center',
      id: 'couponId',
      key: 'couponId',
    },
    {
      title: '优惠券名称',
      align: 'center',
      id: 'couponName',
      key: 'couponName',
    },
    {
      title: '操作',
      align: 'center',
      width: 50,
      render: (record) => (
        <div
          style={{
            textAlign: 'center',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          {isDetail ? null : (
            <ButtonTip title="删除">
              <IconButton
                aria-label="delete"
                color="inherit"
                onClick={() => handleDeleteCoupon(record.id)}
              >
                <DeleteIcon
                  fontSize="small"
                  style={{ color: theme.palette.primary.main }}
                />
              </IconButton>
            </ButtonTip>
          )}
        </div>
      ),
    },
  ];

  return (
    <AccountActivityDetailStyled theme={theme}>
      <Box
        data-log={JSON.stringify({
          oid: 'page_marketing_account_activity_detail',
          isPage: true,
          params: {
            initUrl: window.location.origin + window.location.pathname,
          },
          events: ['_pv', '_pd'],
        })}
        height="100%"
      >
        <Box
          borderRadius="12px"
          bgcolor="background.paper"
          pt={4}
          pr={5}
          pb={5}
          pl={5}
        >
          <Box className="account-activity-detail-title">
            {getPageTitle(opt)}
          </Box>
          <form
            ref={formRef}
            onSubmit={handleSubmit(handleSubmitSuccess, handleSubmitFail)}
          >
            <Box>
              <Controller
                control={control}
                name="name"
                rules={{
                  required: '活动名称是必填项',
                }}
                render={(field) => (
                  <Box width="640px" mb={4}>
                    <Box className="account-activity-detail-field-label">
                      <span style={{ color: 'red' }}>*</span>活动名称
                    </Box>
                    <TextField
                      value={field.value || ''}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      fullWidth
                      variant="standard"
                      placeholder="请输入活动名称"
                      error={Boolean(errors.name)}
                      helperText={errors.name?.message}
                      disabled={isDetail}
                    />
                  </Box>
                )}
              />
            </Box>
            <Box>
              <Controller
                control={control}
                name="date"
                rules={{
                  validate: validateDateRange,
                }}
                render={(field) => (
                  <Box width="640px" height="66px" mb={4}>
                    <DateRangePickerML
                      startText={
                        <>
                          <span style={{ color: 'red' }}>*</span>开始时间
                        </>
                      }
                      endText={
                        <>
                          <span style={{ color: 'red' }}>*</span>结束时间
                        </>
                      }
                      defaultStartDate={
                        detail.dateBegin
                          ? dayjs(detail.dateBegin).format('YYYY-MM-DD')
                          : null
                      }
                      defaultEndDate={
                        detail.dateEnd
                          ? dayjs(detail.dateEnd).format('YYYY-MM-DD')
                          : null
                      }
                      rp={field}
                      disabled={isDetail}
                    />
                    <FormHelperText error={Boolean(errors.date)}>
                      {errors.date?.message}
                    </FormHelperText>
                  </Box>
                )}
              />
            </Box>
            <Box>
              <Controller
                control={control}
                name="code"
                rules={{
                  required: 'utm_activityId是必填项',
                }}
                render={(field) => (
                  <Box width="640px" mb={4}>
                    <Box className="account-activity-detail-field-label">
                      <span style={{ color: 'red' }}>*</span>utm_activityId
                    </Box>
                    <TextField
                      value={field.value || ''}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      fullWidth
                      variant="standard"
                      placeholder="请输入utm_activityId"
                      error={Boolean(errors.code)}
                      helperText={errors.code?.message}
                      disabled={isDetail}
                    />
                  </Box>
                )}
              />
            </Box>
            <Box mt={6} mb={2}>
              <Controller
                control={control}
                name="couponRelated"
                render={(field) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(field.value)}
                        onChange={(e) => field.onChange(e.target.checked)}
                        color="primary"
                        disabled={isDetail}
                      />
                    }
                    label="关联优惠券"
                  />
                )}
              />
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={handleAddCoupon}
                disabled={isDetail}
                style={{ marginLeft: 16 }}
              >
                添加
              </Button>
            </Box>
            <Box mt={2} className="account-activity-detail-coupon-table">
              <DataTableNativeML
                columns={couponTableColumns}
                data={couponList}
                paging={false}
                pageButton={false}
                columnKey="id"
                devicesMinWidth="auto"
              />
            </Box>
          </form>
        </Box>

        <Box className="account-activity-detail-actions">
          {!isDetail && (
            <Button
              variant="contained"
              color="primary"
              type="button"
              onClick={handleFinishClick}
              startIcon={<DoneIcon sx={{ width: '16px', height: '16px' }} />}
            >
              完成
            </Button>
          )}
          <Button
            variant="contained"
            color="secondary"
            onClick={handleExitClick}
            startIcon={<CloseIcon sx={{ width: '16px', height: '16px' }} />}
          >
            退出
          </Button>
        </Box>
      </Box>
      <Dialog
        open={openExitDialog}
        onClose={() => setOpenExitDialog(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogContent>
          <DialogContentText
            id="alert-dialog-description"
            sx={{ minWidth: '200px' }}
          >
            {!isDetail
              ? '退出后将不保存设置内容，确定要退出吗？'
              : '确定要退出吗？'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant="text"
            sx={{ color: '#999', width: '57px' }}
            onClick={() => setOpenExitDialog(false)}
          >
            取消
          </Button>
          <Button
            variant="text"
            sx={{ width: '57px' }}
            onClick={handleExitConfirm}
            autoFocus
          >
            确定
          </Button>
        </DialogActions>
      </Dialog>
      <CouponModal
        open={openCouponModal}
        onClose={handleCloseCouponModal}
        onConfirm={handleCouponConfirm}
        defaultCouponList={couponList}
      />
    </AccountActivityDetailStyled>
  );
};
